import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CardContent,
  Grid,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import GlassCard from '../Cards/GlassCard';
import { dashboardTooltipSurfaceSx, panelMutedSurfaceSx } from '../../utils/colors';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import TrackChangesRoundedIcon from '@mui/icons-material/TrackChangesRounded';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from 'recharts';
import axiosInstance from '../../services/axiosInstance';
import {
  PRIMARY,
  SUCCESS,
  WARNING,
  DANGER,
  gradientTextSx,
} from '../../utils/colors';

// ── Constants ─────────────────────────────────────────────────────────────────

const FILTERS = [
  { key: '7d',     label: 'Last 7 days'  },
  { key: '30d',    label: 'Last 30 days' },
  { key: 'custom', label: 'Custom range' },
];

const todayIso = () => new Date().toISOString().slice(0, 10);

const daysAgoIso = (days) => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
};

const resolveRange = (filter, customStart, customEnd) => {
  if (filter === '7d')     return { startDate: daysAgoIso(6),  endDate: todayIso() };
  if (filter === '30d')    return { startDate: daysAgoIso(29), endDate: todayIso() };
  return { startDate: customStart, endDate: customEnd };
};

// ── Derived analytics summary from graphData ─────────────────────────────────
/**
 * Single source of truth for KPI values.
 * All fields are derived from `graphData` which is already range-filtered.
 *
 * Previously global fields (now range-derived):
 *   - totalSolvedInRange    ← was overview.totalSolved  (lifetime)
 *   - accuracyInRange       ← was overview.accuracy     (lifetime)
 *   - activeDaysInRange     ← was summary.solvedLastWeek/Month (hardcoded windows)
 *   - totalSubmissionsInRange  ← was NOT shown in cards
 *   - acceptedInRange          ← was NOT shown in cards
 *   - avgSolvedPerActiveDay    ← was NOT shown in cards
 *
 * Still global (from overview API, only for streak):
 *   - currentStreak  — shown only for '7d' and '30d' presets
 */
const deriveFilteredSummary = (graphData, filter, overviewStreak) => {
  const dailyData   = graphData?.problemsSolvedPerDay    || [];
  const ratio       = graphData?.submissionsVsAccepted   || { submissions: 0, accepted: 0 };
  const difficulty  = graphData?.difficultyDistribution  || { easy: 0, medium: 0, hard: 0 };

  const totalSolvedInRange = dailyData.reduce((s, d) => s + (d.solved ?? 0), 0);
  const activeDaysInRange  = dailyData.filter((d) => (d.solved ?? 0) > 0).length;
  const totalSubmissionsInRange = ratio.submissions ?? 0;
  const acceptedInRange         = ratio.accepted    ?? 0;

  const accuracyInRange = totalSubmissionsInRange > 0
    ? Number(((acceptedInRange / totalSubmissionsInRange) * 100).toFixed(1))
    : 0;

  const avgSolvedPerActiveDay = activeDaysInRange > 0
    ? Number((totalSolvedInRange / activeDaysInRange).toFixed(1))
    : 0;

  const difficultyDistributionInRange = {
    easy:   difficulty.easy   ?? 0,
    medium: difficulty.medium ?? 0,
    hard:   difficulty.hard   ?? 0,
  };

  // Streak is only meaningful for rolling presets, not custom ranges
  const streakValue   = filter === 'custom' ? null : (overviewStreak ?? 0);
  const showStreak    = filter !== 'custom';

  return {
    totalSolvedInRange,
    totalSubmissionsInRange,
    acceptedInRange,
    accuracyInRange,
    activeDaysInRange,
    avgSolvedPerActiveDay,
    difficultyDistributionInRange,
    streakValue,
    showStreak,
  };
};

// ── Sub-components ────────────────────────────────────────────────────────────

const SummaryCard = ({ icon, label, value, color, tooltip }) => (
  <GlassCard sx={{ borderRadius: 3, height: '100%' }}>
    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, '&:last-child': { pb: 2 } }}>
      <Box
        sx={{
          p: 1, borderRadius: 2, bgcolor: `${color}1a`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}
      >
        {React.cloneElement(icon, { sx: { color, fontSize: 22 } })}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h6" fontWeight={800} lineHeight={1}>
          {value}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary" noWrap>
            {label}
          </Typography>
          {tooltip && (
            <Tooltip title={tooltip} arrow>
              <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled', flexShrink: 0 }} />
            </Tooltip>
          )}
        </Box>
      </Box>
    </CardContent>
  </GlassCard>
);

const ChartCard = ({ title, children }) => (
  <GlassCard sx={{ borderRadius: 3, height: '100%' }}>
    <CardContent sx={{ p: 2, height: '100%', '&:last-child': { pb: 2 } }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
        {title}
      </Typography>
      {children}
    </CardContent>
  </GlassCard>
);

const CustomChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{ ...dashboardTooltipSurfaceSx, borderRadius: 2, p: 1 }}>
      {label && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          {label}
        </Typography>
      )}
      {payload.map((entry, idx) => (
        <Typography key={idx} variant="body2" sx={{ color: entry.color, fontWeight: 600 }}>
          {entry.name}: {entry.value}
        </Typography>
      ))}
    </Box>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const LeetCodeStudentAnalyticsSection = ({ student }) => {
  const [filter, setFilter] = useState('30d');
  const [customStart, setCustomStart]               = useState(daysAgoIso(29));
  const [customEnd,   setCustomEnd]                 = useState(todayIso());
  const [appliedCustomStart, setAppliedCustomStart] = useState(daysAgoIso(29));
  const [appliedCustomEnd,   setAppliedCustomEnd]   = useState(todayIso());

  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [graphData, setGraphData] = useState(null);
  // overview is fetched ONCE (no range params) only for global streak
  const [overviewStreak, setOverviewStreak] = useState(null);

  // ── Active range ───────────────────────────────────────────────────────────
  const activeRange = useMemo(
    () => resolveRange(filter, appliedCustomStart, appliedCustomEnd),
    [filter, appliedCustomStart, appliedCustomEnd]
  );

  // ── Validation ─────────────────────────────────────────────────────────────
  const customRangeError = useMemo(() => {
    if (filter !== 'custom') return '';
    const start = Date.parse(customStart);
    const end   = Date.parse(customEnd);
    if (Number.isNaN(start) || Number.isNaN(end)) return 'Choose valid dates.';
    if (start > end) return 'Start date must be on or before end date.';
    return '';
  }, [filter, customStart, customEnd]);

  // ── Fetch overview once (for global streak only) ──────────────────────────
  useEffect(() => {
    if (!student?.profileId) return;
    axiosInstance
      .get(`/students/${student.profileId}/leetcode/analytics`)
      .then((res) => setOverviewStreak(res.data?.currentStreak ?? 0))
      .catch(() => setOverviewStreak(0));
  }, [student?.profileId]);

  // ── Fetch graph data whenever range changes ───────────────────────────────
  useEffect(() => {
    if (!student?.profileId) return;
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const graphsRes = await axiosInstance.get(
          `/students/${student.profileId}/leetcode/graphs`,
          { params: { startDate: activeRange.startDate, endDate: activeRange.endDate } }
        );
        if (!isMounted) return;
        setGraphData(graphsRes.data || null);
      } catch (err) {
        if (!isMounted) return;
        setError(err.response?.data?.message || 'Failed to load LeetCode analytics.');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();
    return () => { isMounted = false; };
  }, [student?.profileId, activeRange.startDate, activeRange.endDate]);

  // ── Single derived summary — single source of truth for ALL cards + charts ─
  const summary = useMemo(
    () => deriveFilteredSummary(graphData, filter, overviewStreak),
    [graphData, filter, overviewStreak]
  );

  // ── Chart-ready data ───────────────────────────────────────────────────────
  const difficultyPieData = useMemo(() => [
    { name: 'Easy',   value: summary.difficultyDistributionInRange.easy,   color: SUCCESS },
    { name: 'Medium', value: summary.difficultyDistributionInRange.medium, color: WARNING },
    { name: 'Hard',   value: summary.difficultyDistributionInRange.hard,   color: DANGER  },
  ], [summary]);

  const ratioBarData = useMemo(() => [{
    name:        'Submission Success',
    submissions: summary.totalSubmissionsInRange,
    accepted:    summary.acceptedInRange,
  }], [summary]);

  const rangeLabel = `${graphData?.startDate || activeRange.startDate} → ${graphData?.endDate || activeRange.endDate}`;
  const isCustom   = filter === 'custom';

  // ── Card definitions (range-aware labels) ─────────────────────────────────
  const kpiCards = [
    {
      icon:    <QueryStatsRoundedIcon />,
      label:   'Synced Problems Solved',
      value:   summary.totalSolvedInRange,
      color:   PRIMARY,
      tooltip: `Unique problems detected via submission sync within ${rangeLabel}`,
    },
    {
      icon:    <TrackChangesRoundedIcon />,
      label:   'Submission Accuracy',
      value:   `${summary.accuracyInRange}%`,
      color:   SUCCESS,
      tooltip: `Accepted / total submissions within ${rangeLabel}`,
    },
    ...(summary.showStreak
      ? [{
          icon:    <LocalFireDepartmentRoundedIcon />,
          label:   'Current Streak',
          value:   summary.streakValue ?? 0,
          color:   WARNING,
          tooltip: 'Global solving streak (calendar days)',
        }]
      : [{
          icon:    <CheckCircleOutlineRoundedIcon />,
          label:   'Accepted Submissions',
          value:   summary.acceptedInRange,
          color:   SUCCESS,
          tooltip: `Accepted submissions within ${rangeLabel}`,
        }]
    ),
    {
      icon:    <CalendarMonthRoundedIcon />,
      label:   isCustom ? 'Active Days in Range' : (filter === '7d' ? 'Active Days (7d)' : 'Active Days (30d)'),
      value:   summary.activeDaysInRange,
      color:   PRIMARY,
      tooltip: `Days with at least one accepted submission in ${rangeLabel}`,
    },
    {
      icon:    <TrendingUpRoundedIcon />,
      label:   'Total Submissions',
      value:   summary.totalSubmissionsInRange,
      color:   DANGER,
      tooltip: `All submissions (any status) within ${rangeLabel}`,
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* ── Filter bar ──────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ ...gradientTextSx }}>
          LeetCode Analytics
        </Typography>

        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_e, value) => value && setFilter(value)}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none', fontWeight: 600, fontSize: '0.75rem',
              borderRadius: '8px !important',
            },
          }}
        >
          {FILTERS.map((item) => (
            <ToggleButton key={item.key} value={item.key}>{item.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* ── Custom range inputs ─────────────────────────────────────────── */}
      {filter === 'custom' && (
        <Box
          sx={{
            mb: 2, p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider',
            ...panelMutedSurfaceSx, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center',
          }}
        >
          <TextField
            label="Start" type="date" size="small" value={customStart}
            onChange={(e) => setCustomStart(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }}
          />
          <TextField
            label="End" type="date" size="small" value={customEnd}
            onChange={(e) => setCustomEnd(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }}
          />
          <Button
            variant="contained" size="small"
            onClick={() => { setAppliedCustomStart(customStart); setAppliedCustomEnd(customEnd); }}
            disabled={Boolean(customRangeError)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Apply
          </Button>
          {customRangeError && (
            <Typography variant="caption" color="error">{customRangeError}</Typography>
          )}
        </Box>
      )}

      {/* ── Loading ─────────────────────────────────────────────────────── */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress size={30} />
        </Box>
      )}

      {/* ── Error ───────────────────────────────────────────────────────── */}
      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {/* ── Content ─────────────────────────────────────────────────────── */}
      {!loading && !error && (
        <>
          {/* KPI Cards — all derived from same filtered graphData */}
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {kpiCards.map((card) => (
              <Grid item xs={12} sm={6} md={4} key={card.label}>
                <SummaryCard {...card} />
              </Grid>
            ))}
          </Grid>

          {/* Charts */}
          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <ChartCard title="Problems Solved Per Day">
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart
                    data={graphData?.problemsSolvedPerDay || []}
                    margin={{ top: 5, right: 16, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.07)" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={28} />
                    <RechartsTooltip content={<CustomChartTooltip />} />
                    <Legend />
                    <Line
                      type="monotone" dataKey="solved" name="Solved"
                      stroke={PRIMARY} strokeWidth={2} dot={false} activeDot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <ChartCard title="Difficulty Distribution">
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={difficultyPieData} dataKey="value" nameKey="name"
                      cx="50%" cy="50%" outerRadius={72}
                      label={({ name, value }) => `${name}: ${value}`}
                      labelLine={false}
                    >
                      {difficultyPieData.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip content={<CustomChartTooltip />} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <ChartCard title="Submission Success Ratio">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={ratioBarData} margin={{ top: 5, right: 16, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.07)" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={28} />
                    <RechartsTooltip content={<CustomChartTooltip />} />
                    <Legend />
                    <Bar dataKey="submissions" name="Submissions" fill={PRIMARY} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="accepted"    name="Accepted"    fill={SUCCESS} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>
          </Grid>

          {/* Range footer */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5 }}>
            <TrendingUpRoundedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              All metrics above reflect data from {rangeLabel}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

export default LeetCodeStudentAnalyticsSection;
